import React, { useCallback, useEffect } from "react";
import DadosBasicosCompradorSantanderForm from "./DadosBasicosCompradorSantanderForm";
import { useDispatch, useSelector } from "react-redux";
import {
  getComplementoOperacao,
  isGravandoComplementoOperacao,
} from "../../../../../selectors/operacao.selectors";
import useCompleteForm from "../../../../../hooks/useCompleteForm";
import validators from "../../../../../utils/validators";
import { actions } from "../../../../../actions/operacao.actions";
import {
  removeNonDigitsFromString,
  transformToDate,
} from "../../../../../utils/basic";
import formatters from "../../../../../utils/formatters";

const DadosBasicosCompradorSantanderFormConnected = ({
  handleGoTo2,
  events,
  participante: comprador,
  setParticipante,
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const {
    operacao,
    dominios: {
      nacionalidade: nacionalidades,
      genero: generos,
      documentoPessoal: documentosPessoais,
    },
  } = useSelector(getComplementoOperacao);
  const submitting = useSelector(isGravandoComplementoOperacao);

  const callback = useCallback(
    (data) => {
      const c = data.operacao.participantes.find((p) => p.id === comprador.id);
      if (!!c) {
        setParticipante({
          ...c,
          fluxo: data.validacao.completa.participantes.find(
            (f) => f.id === c?.id
          )?.etapas,
        });
      }
      handleGoTo2();
    },
    [handleGoTo2, setParticipante, comprador]
  );

  const [formProps, handleSubmit] = useCompleteForm({
    rules: () => ({
      cpfCnpj: validators.cpfCnpj({ required: true }),
      nome: validators.string({
        required: true,
        maxLength: 100,
        custom: {
          nomeCompleto: (value) =>
            !value ||
            (value.indexOf(" ") > 0 &&
              value.substring(value.indexOf(" ") + 1).length > 0) ||
            "Nome informado deve ser completo",
        },
      }),
      dataNascimento: validators.date({
        required: true,
        min: "01/01/1900",
        max: formatters.dates.short(new Date()),
      }),
      nacionalidade: validators.object({ required: true }),
      email: validators.email({ required: true }),
      nomeMae: validators.string({ required: true, maxLength: 100 }),
      genero: validators.object({ required: true }),
      documentoPessoal: validators.object({
        required: true,
        custom: {
          rgRne: (v) => {
            const doc = documentosPessoais.find((d) => d.id === v?.id);

            if (doc?.tipo === "RG" || doc?.tipo === "RE") {
              return true;
            }

            return "O Santander exige que seja utilizado RG ou RNE";
          },
        },
      }),
      numeroDocumentoPessoal: validators.string({
        required: true,
        maxLength: 100,
      }),
      orgaoDocumentoPessoal: validators.string({
        required: true,
        maxLength: 100,
      }),
      ufOrgaoDocumentoPessoal: validators.string({
        required: true,
      }),
      dataExpedicaoDocumentoPessoal: validators.date({
        required: true,
        min: "01/01/1900",
        max: formatters.dates.short(new Date()),
      }),
      naturalidade: validators.string({
        required: true,
        maxLength: 100,
        custom: {
          validTextSlashText: (value) =>
            !value ||
            /^[A-Za-zÀ-ÖØ-öø-ÿ\s]+ \/ [A-Za-zÀ-ÖØ-öø-ÿ\s]+$/.test(value) ||
            'É necessário inserir a cidade e UF no formato: "Cidade de Nascimento / UF".',
        },
      }),
      nomeProcurador: validators.string({ required: true }),
      cpfProcurador: validators.cpf({ required: true }),
      telefone: validators.phone({ required: true }),
    }),
    initialValues: useCallback(
      () => ({
        possuiProcuracao: false,
        cpfCnpj: "",
        nome: "",
        dataNascimento: "",
        nomeMae: "",
        telefoneResidencial: "",
        nacionalidade: null,
        genero: null,
        documentoPessoal: null,
        email: "",
        telefone: "",
        numeroDocumentoPessoal: "",
        orgaoDocumentoPessoal: "",
        ufOrgaoDocumentoPessoal: "",
        dataExpedicaoDocumentoPessoal: "",
        naturalidade: "",
        nomeProcurador: "",
        cpfProcurador: "",
        ...comprador,
      }),
      [comprador]
    ),
    handleSubmit: useCallback(
      (values) => {
        dispatch(
          actions.gravarComplemento.request({
            callback,
            instituicao: operacao.codigoInstituicao,
            passo: "dados-pessoais",
            participante: {
              id: comprador?.id,
              possuiProcuracao: values.possuiProcuracao,
              nomeParticipante: values.nome,
              dataNascimento: transformToDate(values.dataNascimento),
              nomeMae: values.nomeMae,
              telefoneResidencial: values.telefoneResidencial,
              idNacionalidade: values.nacionalidade?.id,
              idGenero: values.genero?.id,
              idDocumentoPessoal: values.documentoPessoal?.id,
              numeroDocumentoPessoal: values.numeroDocumentoPessoal,
              orgaoDocumentoPessoal: values.orgaoDocumentoPessoal,
              ufOrgaoDocumentoPessoal:
                values.documentoPessoal?.descricao === "RNE"
                  ? null
                  : values.ufOrgaoDocumentoPessoal,
              dataExpedicaoDocumentoPessoal: transformToDate(
                values.dataExpedicaoDocumentoPessoal
              ),
              email: values.email,
              telefone: values.telefone,
              naturalidade: values.naturalidade,
              nomeProcurador: values.possuiProcuracao
                ? values.nomeProcurador
                : null,
              cpfProcurador: values.possuiProcuracao
                ? removeNonDigitsFromString(values.cpfProcurador)
                : null,
            },
            operacao: {
              id: operacao.id,
            },
          })
        );
      },
      [dispatch, comprador, operacao, callback]
    ),
  });

  const { reset } = formProps;

  useEffect(() => {
    reset({
      possuiProcuracao: false,
      cpfCnpj: "",
      nome: "",
      dataNascimento: "",
      nacionalidade: null,
      naturalidade: "",
      genero: null,
      documentoPessoal: null,
      email: "",
      telefone: "",
      nomeMae: "",
      telefoneResidencial: "",
      numeroDocumentoPessoal: "",
      orgaoDocumentoPessoal: "",
      ufOrgaoDocumentoPessoal: "",
      dataExpedicaoDocumentoPessoal: "",
      nomeProcurador: "",
      cpfProcurador: "",
      ...comprador,
    });
  }, [comprador, reset, operacao]);

  const possuiProcuracao = formProps.watch("possuiProcuracao");
  const documentoPessoal = formProps.watch("documentoPessoal");
  const isEstrangeiro = documentoPessoal?.id === 4;

  return (
    <DadosBasicosCompradorSantanderForm
      {...otherProps}
      tipoPessoa={operacao.detalhe.tipoPessoa}
      tipoProduto={operacao.tipoProduto}
      nacionalidades={nacionalidades}
      documentosPessoais={documentosPessoais}
      generos={generos}
      formProps={formProps}
      handleSubmit={handleSubmit}
      submitting={submitting}
      fluxo={comprador.fluxo}
      events={events}
      possuiProcuracao={possuiProcuracao}
      isEstrangeiro={isEstrangeiro}
    />
  );
};

export default DadosBasicosCompradorSantanderFormConnected;
